import createClient, { FetchResponse, Middleware } from "openapi-fetch";
import { components, paths } from "./generated/generated-api";
import { doFetch, HttpError, process, saveMiddlewareConfiguration } from "am-tax-fe-core";
import { fetchApiUrl } from "./apiUtils";
import { getAppState } from "../state/useAppState";
import { ensureSuccessfulResponse } from "../utils/apiKeyErrors";

let configured = false;
let currentClient = createClient<paths, "application/json">({ baseUrl: "" });
let currentClientV2 = createClient<paths, "application/json">({ baseUrl: "" });

export let cachedApiUrl: string | null = null;

(async () => {
    try {
        cachedApiUrl = await fetchApiUrl();
    } catch {
        // Tests and bootstrap races can import this module before /env.json is available.
        // Requests resolve the configured API URL later through the normal app initialization path.
    }
})();

export type StateEnumUnion = "pending" | "processing" | "completed" | "failed"; // TODO: get from OpenAPI
export enum StateEnum {
    Processing = "processing",
    Pending = "pending",
    Failed = "failed",
    Completed = "completed",
}

export type DocumentInfo = components["schemas"]["DocumentInfo"];
export type DocumentListResponse = components["schemas"]["DocumentListResponse"];
export type FilteredDocumentServiceInfo = components["schemas"]["FilteredDocumentServiceInfo"];
export type FilteredDocumentListResponse = components["schemas"]["FilteredDocumentListResponse"];

export type AsyncDocumentUploadResponse = {
    data: {
        documentId: string;
        jobId: string;
        status: string;
        fileName: string;
        createdAt: string;
        message: string;
    };
};

export type JobStatusResponse = {
    data: {
        documentId: string;
        jobId: string;
        status: string;
        fileName: string;
        errorMessage?: string | null;
    };
};

// Custom type that matches actual API response (uses sourceFileNameWithExtension)
export type ActualDocumentInfo = {
    documentId: string;
    sourceFileNameWithExtension: string;
    sanitizedFileNameWithExtension: string;
    sourceFullPath: string;
    projectId: string;
    createdByEmail: string;
    createdAt: string;
    fileSize: number;
    status: string;
    title?: string | null;
    author?: string | null;
    pageCount?: number | null;
    language?: string | null;
    ocrConfidence?: number | null;
    tags: string[];
    hasFormFields: boolean;
    formFieldCount: number;
    searchable: boolean;
};

export type ActualDocumentListResponse = {
    data: ActualDocumentInfo[];
    total: number;
};

export type ProjectData = components["schemas"]["ProjectData"];
export type ProjectRequest = components["schemas"]["ProjectRequest"];
export type ProjectUpdateRequest = components["schemas"]["ProjectUpdateRequest"];
export type ProjectResponse = components["schemas"]["ProjectResponse"];
export type ProjectListResponse = components["schemas"]["ProjectListResponse"];
export type AskDocumentRequest = components["schemas"]["AskDocumentRequest"];
export type AskDocumentResponse = components["schemas"]["AskDocumentResponse"];
export type AskProjectRequest = components["schemas"]["AskProjectRequest"];
export type AskProjectResponse = components["schemas"]["AskProjectResponse"];
export type AskRequest = components["schemas"]["AskRequest"];
export type AskResponse = components["schemas"]["AskResponse"];
export type SuccessResponse = components["schemas"]["SuccessResponse"];
export type ModelsResponse = components["schemas"]["ModelsResponse"];
export type RegionResponse = components["schemas"]["RegionResponse"];
export type ModelInfo = components["schemas"]["ModelInfo"];

// Application types (custom until OpenAPI is regenerated)
export type ApplicationValidationRequest = {
    apiKey: string;
};

export type ApplicationValidationResponse = {
    applicationId: string;
    name: string;
    description?: string;
    status: string;
    createdByEmail?: string;
    createdAt?: string;
    updatedAt?: string;
    isSharepointManaged?: boolean;
    enableExperimentalFeatures?: boolean;
    isAdmin?: boolean;
    sbu?: string;
};

export type TotalCostResponse = {
    totalCost: number;
    currency: string;
};

// Admin types
export type AdminApiKeyDetail = {
    id: string;
    apiKeyPrefix: string;
    isActive: boolean;
    createdAt?: string;
    createdByEmail?: string;
    owner?: string;
    expiresAt?: string;
};

export type AdminApplicationDetail = {
    id: string;
    name: string;
    sbu?: string;
    region?: string;
    owner?: string;
    isAdmin: boolean;
    activeKeysCount: number;
    totalCost?: number;
    currency?: string;
    documentIntelligenceCost?: number;
    totalPagesAnalyzed?: number;
    status: string;
    createdAt?: string;
    createdByEmail?: string;
    apiKeys: AdminApiKeyDetail[];
    hybridSearchEnabled: boolean;
};

export type AdminApplicationListResponse = {
    applications: AdminApplicationDetail[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
};

export type RevokeApiKeyRequest = {
    confirm: boolean;
};

export type RevokeApiKeyResponse = {
    success: boolean;
    message: string;
    keyId: string;
};

// Admin application model types (custom until OpenAPI is regenerated)
export type AdminApplicationModelDetail = {
    id: string;
    modelName: string;
    status: string;
    applicationId: string;
    applicationName?: string;
    createdByEmail?: string;
    createdAt?: string;
};

export type AdminApplicationModelListResponse = {
    items: AdminApplicationModelDetail[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
};

export type CreateApplicationModelRequest = {
    modelName: string;
    applicationId: string;
    status?: string;
};

export type UpdateApplicationModelStatusRequest = {
    status: string;
};

export type CreateApplicationModelResponse = {
    id: string;
    modelName: string;
    status: string;
    applicationId: string;
    createdAt: string;
    createdByEmail?: string;
};

export type TranscriptionMessage = {
    /** Format: uuid */
    id: string;
    role?: string;
    content: string;
    /** Format: date-time */
    created_datetime: string;
    /** Format: date-time */
    updated_datetime: string;
};

// OCR Playground Types
export type OcrPlaygroundRequest = {
    fileContent: string;
    fileName: string;
    modelId?: string;
    pages?: string;
};

export type OcrPageInfo = {
    pageNumber: number;
    text: string;
    confidence?: number | null;
};

export type OcrPlaygroundResponse = {
    fullText: string;
    pagesCount: number;
    averageConfidence?: number | null;
    pages: OcrPageInfo[];
    engineName: string;
    processingTimeMs?: number | null;
};

export function configureApiClient(baseUrl: string, middlewares?: Middleware[]) {
    const baseUrlV2 = `${baseUrl}/api/v2/`;
    baseUrl = `${baseUrl}/api/`;
    saveMiddlewareConfiguration(middlewares);

    currentClient = createClient<paths, "application/json">({
        baseUrl,
    });
    currentClientV2 = createClient<paths, "application/json">({
        baseUrl: baseUrlV2,
    });

    if (middlewares) {
        currentClient.use(...middlewares);
        currentClientV2.use(...middlewares);
    }
    configured = true;
}

export function client() {
    if (!configured) {
        throw new Error("You must call configureApiClient before making any api calls");
    }
    return currentClient;
}

export function clientV2() {
    if (!configured) {
        throw new Error("You must call configureApiClient before making any api calls");
    }
    return currentClientV2;
}

// Project APIs
export const getProjectsAsync = async (): Promise<ProjectListResponse> => process(client().GET("/v1/projects"));
export const getProjectByIdAsync = async (projectId: string): Promise<ProjectResponse> =>
    process(
        client().GET("/v1/projects/{projectId}", {
            params: {
                path: {
                    projectId: projectId,
                },
            },
        }),
    );
export const createProjectAsync = async (project: ProjectRequest): Promise<ProjectResponse> => process(client().POST("/v1/projects", { body: project }));
export const updateProjectAsync = async (projectId: string, project: ProjectUpdateRequest): Promise<ProjectResponse> =>
    process(
        client().PATCH("/v1/projects/{projectId}", {
            params: {
                path: {
                    projectId,
                },
            },
            body: project,
        }),
    );
export const deleteProjectAsync = async (projectId: string): Promise<SuccessResponse> =>
    process(
        client().DELETE(`/v1/projects/{projectId}`, {
            params: {
                path: {
                    projectId: projectId,
                },
            },
        }),
    );

// Application APIs
export const validateApiKeyAsync = async (apiKey: string): Promise<ApplicationValidationResponse> => {
    const response = await fetch(`${cachedApiUrl}/api/v1/applications/validate-api-key`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            apikey: apiKey,
        },
        body: JSON.stringify({ apiKey }),
    });

    await ensureSuccessfulResponse(response, "Failed to validate API key");

    return response.json();
};

export type CreateApiKeyRequest = {
    name: string;
    prefix: string;
    email: string;
    owner: string;
    sbu?: string;
    description?: string;
    region: string;
};

export type CreateApiKeyResponse = {
    apiKey: string;
    applicationId: string;
    name: string;
    prefix: string;
    createdAt: string;
    createdByEmail: string;
    owner: string;
};

export const createApiKeyAsync = async (request: CreateApiKeyRequest): Promise<CreateApiKeyResponse> => {
    const apiKey = getAppState().apiKey;

    if (!apiKey) {
        throw new Error("No API key found. Please authenticate first.");
    }

    const response = await fetch(`${cachedApiUrl}/api/v1/applications/create-api-key`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            apikey: apiKey,
        },
        body: JSON.stringify(request),
    });

    await ensureSuccessfulResponse(response, `Failed to create API key: ${response.statusText}`);

    return response.json();
};

// Document APIs
export const askAboutDocument = async (args: AskDocumentRequest, documentId: string, signal?: AbortSignal) => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/ask/document/${documentId}/stream`, "", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(args),
        signal,
    });

    await ensureSuccessfulResponse(response, "Failed to ask about document");

    if (!response.body) {
        throw new Error("ReadableStream not supported in this browser.");
    }

    return response.body.getReader();
};

export const askAboutProject = async (args: AskProjectRequest, projectId: string, signal?: AbortSignal) => {
    const appState = getAppState();
    const apiKey = appState.apiKey;
    const apiKeyParam = apiKey ? `?apiKey=${encodeURIComponent(apiKey)}` : "";
    const response = await doFetch(`${cachedApiUrl}/api/v1/ask/project/${projectId}/stream${apiKeyParam}`, "", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(args),
        signal,
    });

    await ensureSuccessfulResponse(response, "Failed to ask about project");

    if (!response.body) {
        throw new Error("ReadableStream not supported in this browser.");
    }

    return response.body.getReader();
};

export const askGeneric = async (args: AskRequest, signal?: AbortSignal) => {
    const response = await doFetch(`${cachedApiUrl}/api/v2/ask/stream`, "", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(args),
        signal,
    });

    await ensureSuccessfulResponse(response, "Failed to ask AI");

    if (!response.body) {
        throw new Error("ReadableStream not supported in this browser.");
    }

    return response.body.getReader();
};

export const getProjectDocumentsAsync = async (projectId: string): Promise<components["schemas"]["DocumentListResponse"]> =>
    process(
        client().GET("/v1/projects/{projectId}/documents", {
            params: {
                path: {
                    projectId: projectId,
                },
            },
        }),
    );

export const getApplicationDocumentsAsync = async (): Promise<DocumentListResponse> => process(client().GET("/v1/documents"));

export const getDocumentByIdAsync = async (documentId: string): Promise<DocumentInfo> => {
    const response = await process(
        client().GET("/v1/documents/{documentId}", {
            params: {
                path: {
                    documentId,
                },
            },
        }),
    );
    return response.data;
};

const INITIAL_DOCUMENT_JOB_POLL_DELAY_MS = 5000;
const MAX_DOCUMENT_JOB_POLL_DELAY_MS = 10000;
const DOCUMENT_JOB_POLL_TIMEOUT_MS = 300000;

const waitForDocumentJobPoll = (delayMs: number): Promise<void> => new Promise(resolve => setTimeout(resolve, delayMs));

type DocumentJobClient = {
    GET: (
        path: "/documents/{documentId}/job",
        options: { params: { path: { documentId: string } } },
    ) => Promise<FetchResponse<unknown, unknown, "application/json">>;
    POST: {
        (path: "/documents", options: { body: components["schemas"]["DocumentUploadRequest"] }): Promise<FetchResponse<unknown, unknown, "application/json">>;
        (
            path: "/projects/{projectId}/documents",
            options: {
                params: { path: { projectId: string } };
                body: components["schemas"]["ProjectDocumentUploadRequest"];
            },
        ): Promise<FetchResponse<unknown, unknown, "application/json">>;
    };
};

const documentJobClient = () => clientV2() as unknown as DocumentJobClient;

export const uploadDocumentAsync = async (request: components["schemas"]["DocumentUploadRequest"]): Promise<AsyncDocumentUploadResponse> =>
    process(documentJobClient().POST("/documents", { body: request })) as Promise<AsyncDocumentUploadResponse>;

export const uploadDocumentToProjectAsync = async (
    projectId: string,
    request: components["schemas"]["ProjectDocumentUploadRequest"],
): Promise<AsyncDocumentUploadResponse> =>
    process(
        documentJobClient().POST("/projects/{projectId}/documents", {
            params: {
                path: {
                    projectId,
                },
            },
            body: request,
        }),
    ) as Promise<AsyncDocumentUploadResponse>;

export const pollDocumentJobAsync = async (documentId: string): Promise<JobStatusResponse> => {
    const startedAt = Date.now();
    let delayMs = INITIAL_DOCUMENT_JOB_POLL_DELAY_MS;

    while (Date.now() - startedAt < DOCUMENT_JOB_POLL_TIMEOUT_MS) {
        let response: JobStatusResponse;
        try {
            response = (await process(
                documentJobClient().GET("/documents/{documentId}/job", {
                    params: {
                        path: {
                            documentId,
                        },
                    },
                }),
            )) as JobStatusResponse;
        } catch (error) {
            // process() throws for the 422 FAILED response instead of returning a body, so the errorMessage must be read from the error's nested data.
            if (error instanceof HttpError) {
                const failedResponse = error.data as JobStatusResponse | undefined;
                throw new Error(failedResponse?.data.errorMessage || "Document processing failed");
            }
            throw error;
        }

        if (response.data.status === "COMPLETED") {
            return response;
        }

        await waitForDocumentJobPoll(delayMs);
        delayMs = Math.min(delayMs * 2, MAX_DOCUMENT_JOB_POLL_DELAY_MS);
    }

    throw new Error("Document processing timed out");
};

export const deleteDocumentAsync = async (documentId: string): Promise<SuccessResponse> =>
    process(
        client().DELETE("/v1/documents/{documentId}", {
            params: {
                path: {
                    documentId,
                },
            },
        }),
    );

export const downloadDocumentAsync = async (documentId: string): Promise<Blob> => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/documents/${documentId}/document`, "", {
        method: "GET",
    });
    await ensureSuccessfulResponse(response, "Failed to download document");
    return response.blob();
};

// Models API
export const getAvailableModelsAsync = async (): Promise<ModelsResponse> => process(client().GET("/v1/models"));

// Regions API
export const getAvailableRegionsAsync = async (): Promise<RegionResponse> => process(client().GET("/v1/regions"));

// Analytics API
export const getTotalCostAsync = async (): Promise<TotalCostResponse> => process(client().GET("/v1/analytics/total-cost"));

// Admin API
export const getAllApplicationsAsync = async (limit: number = 50, offset: number = 0): Promise<AdminApplicationListResponse> => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/admin/applications?limit=${limit}&offset=${offset}`, "", {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });

    await ensureSuccessfulResponse(response, "Failed to fetch applications");

    return response.json();
};

export const revokeApiKeyAsync = async (keyId: string, request: RevokeApiKeyRequest): Promise<RevokeApiKeyResponse> => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/admin/api-keys/${keyId}/revoke`, "", {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
    });

    await ensureSuccessfulResponse(response, "Failed to revoke API key");

    return response.json();
};

export const getApplicationModelsAsync = async (
    applicationId?: string,
    limit: number = 50,
    offset: number = 0,
    status?: string,
): Promise<AdminApplicationModelListResponse> => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (applicationId) params.set("application_id", applicationId);
    if (status) params.set("status", status);

    const response = await doFetch(`${cachedApiUrl}/api/v1/admin/application-models?${params}`, "", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
    });

    await ensureSuccessfulResponse(response, "Failed to fetch application models");

    return response.json();
};

export const createApplicationModelAsync = async (request: CreateApplicationModelRequest): Promise<CreateApplicationModelResponse> => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/admin/application-models`, "", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
    });

    await ensureSuccessfulResponse(response, "Failed to create application model");

    return response.json();
};

export const updateApplicationModelStatusAsync = async (id: string, request: UpdateApplicationModelStatusRequest): Promise<AdminApplicationModelDetail> => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/admin/application-models/${id}/status`, "", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
    });

    await ensureSuccessfulResponse(response, "Failed to update application model status");

    return response.json();
};

// OCR Playground API
export const analyzeOcrPlaygroundAsync = async (request: OcrPlaygroundRequest): Promise<OcrPlaygroundResponse> => {
    try {
        const response = await doFetch(`${cachedApiUrl}/api/v1/ocr-playground/analyze`, "", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
        });

        await ensureSuccessfulResponse(response, "Failed to analyze document with OCR");

        return response.json();
    } catch (error) {
        // Handle network errors (timeout, connection lost, etc.)
        if (error instanceof TypeError && error.message === "Failed to fetch") {
            throw new Error("Request timed out or connection was lost. Please try again or use a smaller document.");
        }
        // Re-throw other errors
        throw error;
    }
};

// Markdown Conversion APIs
export const convertMarkdownToPdfAsync = async (markdown: string): Promise<Blob> => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/convert-md-to-pdf`, "", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markdown }),
    });
    await ensureSuccessfulResponse(response, "Failed to convert markdown to PDF");
    return response.blob();
};

export const convertMarkdownToWordAsync = async (markdown: string): Promise<Blob> => {
    const response = await doFetch(`${cachedApiUrl}/api/v1/convert-md-to-word`, "", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markdown }),
    });
    await ensureSuccessfulResponse(response, "Failed to convert markdown to Word");
    return response.blob();
};
